import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Subject } from 'rxjs';

// Service
import { HttpService } from '@/Services/http.service';

// Interface
import {
  IAccionBoton,
  IDataPagination,
  IPagination,
} from '@/interface/general/IPagination';
import { ITable, TypeColumn, TypeElement, TypeFormat } from '@/interface/general/ITable';
import { IServerSide } from '@/shared/components/general/table-generic/interface/table-generic.interface';

// Export Excel
import { environment } from 'src/environments/environment';
// Enum
import { EOrigenEventoComponent } from '@/common/enums/general.enum';
// Abstract
import { TableGenericAbstract } from '@/abstract/tableGeneric.abstract';
// Interface
import {
  IExcelResponse,
  IResponseApiSS,
  IServerSideBackend,
  RadicadoClasificacion,
} from '@/interface/general/IResponseApi';

import { GeneralService } from './general.service';

// Componente
import {
  ITimelineDocumentoForm,
  IFormDataTimelineDocumento,
  ITimeLineResponse,
} from '../interface/dinamic-form.interface';
import { BotoneraService } from '../../botonera.service';

@Injectable({
  providedIn: 'root',
})
export class TableService extends TableGenericAbstract<IFormDataTimelineDocumento> {

  // Subject para notificar al componente que debe refrescar la tabla
  refreshTable$ = new Subject<void>();
  // Enum
  RadicadoClasificacion = RadicadoClasificacion;
  // CodProgram
  CodProgram!: string;
  // IdfdArchivo
  IdfdArchivo!: string;
  // Cabecera de la tabla
  tableSubject!: ITable;
  // Parametros del filtro
  paramsObject: IServerSideBackend = {} as IServerSideBackend;
  // RegistrosSeleccionados
  registrosSeleccionados!: ITimelineDocumentoForm[];
  // Subject para los datos de la tabla genérica
  dataTableGenericSubject$ = new BehaviorSubject<
    IDataPagination<ITimelineDocumentoForm[]>
  >({
    data: [],
  });

  // Configuración de paginación
  rowsPerPageOptions = environment.paginationLarge;
  rowsPerPage = this.rowsPerPageOptions[0];
  currentPagination: IPagination = {} as IPagination;

  // URL del endpoint principal

  private urlApi = 'FlujoDoc/GetTimeLineDocumento';

  // Inyección de dependencias
  private http = inject(HttpClient);
  private httpService = inject(HttpService);
  private generalService = inject(GeneralService);
  private botoneraService = inject(BotoneraService);

  constructor() {
    super();
  }

  //#region Configuración de la tabla

 
  async setHeaderTable() {
    const table: ITable = this.getTableConfig();
    this.tableSubject = table;
  }

 
  getTableConfig(): ITable {
    return {
      fieldId: 'Id',
      paginator: true,
      rowsPerPageOptions: this.rowsPerPageOptions,
      lazyLoadOnInit: true,
      columns: [
        {
          field: 'Id',
          label: ' ',
          type: TypeColumn.INDEX,
          filter: false,
          sort: false,          
        },
        {
          field: 'IdfdArchivo',
          label: 'Codigo',
          type: TypeColumn.TOOLTIP,
          filter: false,
          sort: true,
          fieldColorTag: '#4F4CE4',
          tooltipField: [
            'CodfdCabecera','-',
            'NombreAbreviado', '-|-',
            'NombrePaso', 
            'CodGlobal', '-',
            'NombreAgencia', '-|-',
            'IdfdArchivo',
          ],
          style: {
            target: TypeElement.ROW, 
            categorize: [
              { value: this.IdfdArchivo, style: { 'background-color' : '#a2e00d' }}
            ]
          }
        },
        {
          field: 'idPaqueteConcatenadotmp',
          label: 'Paquete',
          type: TypeColumn.STRING,
          filter: false,
          sort: true,
        },
        {
          field: 'NombreDocPermitidos',
          label: 'Documentos',
          type: TypeColumn.TOOLTIP,
          filter: false,
          sort: true,
          tooltipField: ['CodfdDocPermitidos'],
          style: { target: TypeElement.CELL, unique: { 'background-color' : '#a2e00d' } }
        },
        {
          field: 'FechaRegistro',
          label: 'Cargue',
          type: TypeColumn.TOOLTIP,
          filter: false,
          sort: true,
          fieldColorTag: '#807F7F',
          tooltipField: ['NombreArchivo', 'CodUser'],
        },
        {
          field: 'cmpoObservacion',
          label: 'Observacion',
          type: TypeColumn.TRUNCATE_TEXT,
          width: '15%',
          filter: false,
          sort: true,
        },
        {
          field: 'TipoDocumento',
          label: 'TipoDocumento',
          type: TypeColumn.STRING,
          filter: false,
          sort: false,
          style: { 
            target: TypeElement.ROW, 
            categorize: [
              { value: '145', style: { 'background-color' : '#eb6565' } }
            ] 
          }
        },
        {
          field: 'cmpoCodigoUnico',
          label: 'Codigo(Fac)',
          type: TypeColumn.NUMBER,
          filter: false,
          sort: true,
        },
        {
          field: 'strcmpoValor',
          label: 'Valor',
          type: TypeColumn.STRING,
          filter: false,
          sort: true,
        },
        {
          field: 'cmpoCodProveedor',
          label: 'Proveedor',
          type: TypeColumn.TOOLTIP,
          filter: false,
          sort: true,
          tooltipField: ['NombrecmpoCodProveedor'],
        },

        {
          field: 'acciones',
          label: 'Acciones',
          type: TypeColumn.ACTION,
          showActions: false,
          showSelector: false,
          action: (rowData: IFormDataTimelineDocumento, btn: IAccionBoton) =>
            this.handleAction(rowData, btn),
        },
      ],
    };
  }
  //#endregion

  //#region Botoneras Flujodoc
  override async handleAction(record: IFormDataTimelineDocumento, btn: IAccionBoton): Promise<void> {
    const response: boolean = await this.botoneraService.handleAction(record, btn);
    if (response) this.onLoadRecords(this.currentPagination, EOrigenEventoComponent.BTN_TABLE);
  }
  //#endregion


  async onLoadRecords(event: any, origen: EOrigenEventoComponent) {
    // Validación: si no hay evento o está vacío, no hace nada
    if (!event || Object.keys(event).length === 0) {
      return;
    }

    this.currentPagination = event;

    // Adaptar el evento recibido a la interfaz de paginación esperada por el backend
    const adaptedEvent: IPagination = {
      ...event,
      first: event.first ?? 0,
      rows: event.rows ?? this.rowsPerPage,
      sortField: event.sortField ?? '',
      sortOrder: event.sortOrder ?? 0,
      filters: event.filters ?? null,
      globalFilter: event.globalFilter ?? null,
    };

    // Preparar los parámetros de paginación y filtros para el backend
    let serverSide: IServerSide;
    if (origen === EOrigenEventoComponent.BTN_FILTER) {
      // Si el origen es un filtro, adapta los campos según tu modelo de filtros
      // CAMBIA ESTOS CAMPOS SEGÚN TU MODELO Y BACKEND

      serverSide = {
        first: 0,
        rows: this.rowsPerPage,
        sortField: '',
        sortOrder: 0,
        filters: {},
        globalFilter: null,
      };
    } else {
      // Si el origen es la tabla (paginación, orden, etc.)
      const { first, rows, sortOrder, sortField, filters, globalFilter } =
        adaptedEvent;
      serverSide = {
        first,
        rows,
        sortField,
        sortOrder,
        filters,
        globalFilter,
      };
    }

    // Construir el objeto de parámetros para el backend
    // CAMBIA LOS CAMPOS DE ESTE OBJETO SEGÚN TU API Y CREA/ADAPTA LA INTERFAZ IServerSideBackend
    const params: IServerSideBackend = {
      ServerSide: serverSide, // <-- Siempre debe ir la paginación y filtros
      ExportExcel: false, // <-- Si tu API no usa este campo, elimínalo
    };

    // Convierte los parámetros a HttpParams para la petición HTTP
    // Si tu backend espera los parámetros de otra forma, modifica convertToHttpParams
    const httpParams = this.convertToHttpParams(params);

    // Llama al método que hace la petición (real o fake)
    this.callHttpService(httpParams);
  }

  override async callHttpService(httpParams: HttpParams) {
    try {
      this.httpService.GetParams(this.urlApi, httpParams).subscribe({
        next: (resp: IResponseApiSS) => {
          const { data, metadataServerSide } = resp;
          const datosTablaGenericConsultada: IDataPagination<
            ITimelineDocumentoForm[]
          > = {
            data,
            pagination: {
              first:
                metadataServerSide?.numeroPagina *
                metadataServerSide.filasxPagina,
              rows: metadataServerSide.filasxPagina,
            },
            totalRecords: metadataServerSide?.totalRegitros,
          };
          this.totalRecords = datosTablaGenericConsultada?.totalRecords ?? 0;
          this.dataTableGenericSubject$.next(datosTablaGenericConsultada);
        },
        error: async (error) => {
          // Aqui consumes la Fake data
          const fake_data = await lastValueFrom(
            this.http.get<IResponseApiSS>('datapatch.json')
          );

          const { data, metadataServerSide } = fake_data;
          const datosTablaGenericConsultada: IDataPagination<
            ITimelineDocumentoForm[]
          > = {
            data,
            pagination: {
              first:
                metadataServerSide.numeroPagina *
                metadataServerSide.filasxPagina,
              rows: metadataServerSide.filasxPagina,
            },
            totalRecords: metadataServerSide.totalRegitros ?? 0,
          };
          this.totalRecords = metadataServerSide.totalRegitros ?? 0;
          this.dataTableGenericSubject$.next(datosTablaGenericConsultada);
        },
      });
    } catch (error) {
    }
  }

  get currentDataTable(): IDataPagination<ITimelineDocumentoForm[]> {
    return this.dataTableGenericSubject$.getValue();
  }

  override descargarPdf(record: IFormDataTimelineDocumento): void {
    throw new Error('Method not implemented.' + record);
  }

  //#endregion

  override procesarPdf(record: IFormDataTimelineDocumento): void {
    throw new Error('Method not implemented.' + record);
  }
    override newRegister(): void {
    throw new Error('Method not implemented.');
  }
  
  //#region EXCEL
  /**
   * Metodo para exportar registros a excel
   */
  onExportSelectedExcel() {
    this.paramsObject.ExportExcel = true;
    this.exportarExcel(this.convertToHttpParams(this.paramsObject));
  }

  override exportarExcel(httpParams: HttpParams): void {
    this.httpService
      .GetParams<IExcelResponse>(this.generalService.urlGetAll, httpParams)
      .subscribe({
        next: (response) => {
          if (response.estado && response.data) {
            const link = document.createElement('a');
            link.href =
              `data:${response.data.ContentType};base64,` +
              response.data.fileBase64;
            link.download = response.data.NombreArchivo || 'archivo.xlsx';
            link.click();
          }
        },
        error: (err) => {
        },
      });
  }

  //#endregion

  //#region Exportación
  
  onSelected(registrosSeleccionados: ITimelineDocumentoForm[]) {
    this.registrosSeleccionados = registrosSeleccionados;
  }

  //#endregion

  //#region Utilidades y helpers

  /**
   * Convierte un array de parámetros dinámicos a HttpParams.
   * @param paramsObject Array de objetos { key, value }
   */
  dynamicParams(paramsObject: { key: string; value: string }[]): HttpParams {
    let params = new HttpParams();
    paramsObject.forEach((filter) => {
      params = params.append(filter.key, filter.value);
    });
    return params;
  }

  private convertToHttpParams(params: IServerSideBackend): HttpParams {
    let httpParams = new HttpParams();

    // Limpiar filtro global si existe
    if (
      params.ServerSide &&
      params.ServerSide.filters &&
      typeof params.ServerSide.filters === 'object' &&
      (params.ServerSide.filters as { global?: unknown }).global !== undefined
    ) {
      delete (params.ServerSide.filters as { global?: unknown }).global;
    }

    //if (params.IdfdArchivo) {
    httpParams = httpParams.append('IdfdArchivo', this.IdfdArchivo);
    //}

    // Agregar parámetro ServerSide (como string JSON)
    if (params.ServerSide) {
      httpParams = httpParams.append(
        'ServerSide',
        JSON.stringify(params.ServerSide)
      );
    }
    // Agregar parámetro booleano
    httpParams = httpParams.append(
      'ExportExcel',
      params.ExportExcel.toString()
    );
    return httpParams;
  }
  //#endregion

  loadTimeline(idfdArchivo: string) {

    this.IdfdArchivo = idfdArchivo;

    const params: any = {
      IdfdArchivo: Number(idfdArchivo),

      ServerSide: {
        first: 0,
        rows: 1000,
        sortField: 'FechaRegistro',
        sortOrder: -1,
        filters: {},
        globalFilter: null,
      },

      ExportExcel: false,
    };

    const httpParams = this.convertToHttpParams(params);

    return this.httpService.GetParams<ITimeLineResponse>(
      this.urlApi,
      httpParams
    );
  }
  //#endregion
}
